<template>
    <transition name="slide" mode="out-in">
        <v-app v-if="showApp">
            <app-header />
            <app-sidebar />
            <v-main>
                <v-container fluid fill-height pa-0>
                    <v-row no-gutters class="fill-height pa-2">
                        <app-data-loader />
                        <transition name="slide" mode="out-in">
                            <router-view />
                        </transition>
                    </v-row>
                </v-container>
                <app-snackbar
                    :items="notifyQueue"
                    @notify-close="onNotifyClose"
                    @home-page="onHomePage"
                />
                <component
                    :is="dialogComponent"
                    :show="showDialog"
                    @close="onCloseDialog"
                ></component>
            </v-main>
            <app-footer @switch-mode="onSwitchMode" @jobs="onJobsClick" />
        </v-app>
    </transition>
</template>

<script>
// TODO: cleanup features.json and remove the unused features

import AppSidebar from './components/App/AppSidebar';
import AppHeader from './components/App/AppHeader';
import AppFooter from './components/App/AppFooter';
import AppSnackbar from './components/App/AppSnackbar';
import AppDataLoader from './components/App/AppDataLoader';
import { mapState, mapActions, mapGetters } from 'vuex';
const JobsDialog = () => import('./views/jobs/JobsDialog');

export default {
    name: 'App',
    components: {
        'app-sidebar': AppSidebar,
        'app-header': AppHeader,
        'app-footer': AppFooter,
        'app-snackbar': AppSnackbar,
        'app-data-loader': AppDataLoader,
        'jobs-dialog': JobsDialog
    },
    data() {
        return {
            showApp: true,
            showDialog: false,
            dialogComponent: ''
        };
    },
    computed: {
        ...mapState(['isDarkMode', 'jobsDialogOpen']),
        ...mapState('notifier', ['notifyQueue']),
        ...mapGetters('authUser', ['isUserAuthenticated']),
        ...mapState('brand', ['mso'])
    },
    watch: {
        isUserAuthenticated(value) {
            if (!value) {
                this.forwardToLoginPage();
            }
        },
        jobsDialogOpen(value) {
            if (value) {
                setTimeout(() => {
                    this.openJobsDialog();
                }, 1500);
            }
        },
        $route(to) {
            // reset the focus to the app container on route update
            const appTitle = 'Venue Management System';
            const title = to.meta.title
                ? `${to.meta.title} - ${appTitle}`
                : appTitle;
            document.title = title;
            this.$nextTick(() => {
                this.setAppWrapperFocus();
            });
        }
    },
    created() {
        if (this.isDarkMode) {
            this.$vuetify.theme.dark = true;
        } else {
            this.$vuetify.theme.dark = false;
        }
        this.addDataRefreshTimer();
        if (!this.mso) {
            this.saveMso(window.location.hostname);
        }
    },
    beforeDestroy() {
        clearInterval(this.$options.refreshTimer);
    },
    methods: {
        ...mapActions(['saveIsDarkMode']),
        ...mapActions('notifier', {
            removeNotification: 'removeFirst'
        }),
        ...mapActions('devices', ['fetchDeviceStatusSummary']),
        ...mapActions('spaces', ['fetchSpaceStatusSummary']),
        ...mapActions('brand', ['saveMso']),
        // =================== Events ======================
        onSwitchMode() {
            setTimeout(() => {
                this.showApp = false;
                this.saveIsDarkMode(!this.$vuetify.theme.dark);
                this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
            }, 200);
            setTimeout(() => {
                this.showApp = true;
            }, 500);
        },
        onNotifyClose() {
            // we need to remove the first item of the notifyQueue
            if (this.notifyQueue && this.notifyQueue.length > 0) {
                this.removeNotification();
            }
        },
        forwardToLoginPage() {
            // if no token or expired token we need to kickout the user
            setTimeout(() => {
                this.timeout = 0;
                this.snackbar = false;
                if (this.$router.currentRoute.name !== 'loginPage') {
                    this.$router.push({ name: 'loginPage' });
                }
            }, 1000);
        },
        onHomePage() {
            this.$router.push({ name: 'homePage' });
        },
        onJobsClick() {
            this.openJobsDialog();
        },
        onCloseDialog() {
            this.closeJobsDialog();
        },
        // ================== Others =====================
        openJobsDialog() {
            this.dialogComponent = 'jobs-dialog';
            this.showDialog = true;
        },
        closeJobsDialog() {
            this.dialogComponent = '';
            this.showDialog = true;
            this.$store.commit('setJobsDialogOpen', false);
        },
        setAppWrapperFocus() {
            this.$el.focus();
        },
        addDataRefreshTimer() {
            const time = 30 * 60 * 1000; // every 30 mins
            this.$options.refreshTimer = setInterval(() => {
                if (this.isUserAuthenticated) {
                    this.fetchDeviceStatusSummary({});
                    this.fetchSpaceStatusSummary({});
                }
            }, time);
        }
    }
};
</script>

<style lang="scss">
// --------------------- fieldset / legned -----------------
.theme--light {
    fieldset {
        border-radius: 5px;
        border-color: rgba(#000000, 0.3);
    }
    legend {
        color: map-get($grey, 'darken-1');
    }
}
.theme--dark {
    fieldset {
        border-radius: 5px;
        border-color: rgba(#ffffff, 0.3);
    }
    legend {
        color: map-get($grey, 'base');
    }
}
// --------------------- Data Tables -----------------------
@include theme(v-data-table) using ($material) {
    thead tr th {
        border-bottom: medium solid map-get($material, 'dividers') !important;
    }
}
.v-data-table thead th {
    color: var(--v-secondary-base) !important;
    &:hover {
        color: var(--v-secondary-base) !important;
    }
    &.sortable.active {
        color: var(--v-secondary-base) !important;
    }
    .v-data-table-header__icon {
        color: var(--v-secondary-base) !important;
    }
}
table thead tr {
    &:focus {
        outline: 2px solid var(--v-primary-base);
    }
}
// ================================ scrollable section ============================
.app-content-card {
    flex: 1 1 auto;
}
// this class can be used inside the content pages to make
// inner section scrollable if needed
.scrollable-container {
    display: flex !important;
    flex-direction: column;
    flex: 1 1 auto;
    overflow: hidden;
}
// scrollable-centent must be put within scrollable-container
.scrollable-content {
    flex: 1 1 0px;
    overflow: auto !important;
    .v-data-table,
    .v-data-table__wrapper {
        height: 100%;
    }
}
// ================================ Inputs =================================
.v-text-field.v-text-field--prefix.v-input--dense .v-text-field__slot .v-label {
    line-height: 16px;
}
// ================================ transitions ============================
.fade-enter-active,
.fade-leave-active {
    transition-duration: 0.3s;
    transition-property: height, opacity;
    transition-timing-function: ease;
    overflow: hidden;
}
.fade-enter,
.fade-leave-active {
    opacity: 0;
}

.slide-enter,
.slide-leave-active {
    opacity: 0;
    transform: translate(-2em, 0);
}
.slide-enter-active,
.slide-leave-active {
    transition-duration: 0.8s;
    transition-property: height, opacity, transform;
    transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
    overflow: hidden;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition-duration: 0.5s;
    transition-property: height, opacity, transform;
    transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
    overflow: hidden;
}
.slide-left-enter,
.slide-right-leave-active {
    opacity: 0;
    transform: translate(2em, 0);
}
.slide-left-leave-active,
.slide-right-enter {
    opacity: 0;
    transform: translate(-2em, 0);
}

.zoom-enter-active,
.zoom-leave-active {
    animation-duration: 0.5s;
    animation-fill-mode: both;
    animation-name: zoom;
}
.zoom-leave-active {
    animation-direction: reverse;
}
.flip-enter-active {
    transition: all 0.7s ease;
}
.flip-leave-active {
    transition: all 0.1s ease;
}
.flip-enter {
    transform: rotateY(-180deg);
    opacity: 0;
}
@keyframes zoom {
    from {
        opacity: 0;
        transform: scale3d(0.3, 0.3, 0.3);
    }
    100% {
        opacity: 1;
    }
}
</style>
